
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItineraryView } from '@/components/itinerary/ItineraryView';
import type { PersonalizedItineraryOutput } from '@/ai/flows/generate-personalized-itinerary';
import type { ItineraryFormValues } from '@/components/itinerary/ItineraryForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ItineraryDetailsSkeleton } from '@/components/itinerary/details/ItineraryDetailsSkeleton';
import type { Itinerary } from '@/app/lib/types';
import { addDays, format } from 'date-fns';

function NoItineraryFound() {
  return (
    <Card className="w-full max-w-lg mx-auto mt-16">
      <CardHeader>
        <CardTitle className="text-center">No Itinerary to Display</CardTitle>
      </CardHeader>
      <CardContent className="text-center">
        <p className="text-muted-foreground mb-6">
          It looks like you haven't generated an itinerary yet.
        </p>
        <Button asChild>
          <Link to="/itinerary/create">Create a New Itinerary</Link>
        </Button>
      </CardContent>
    </Card>
  );
}


export default function GeneratedItineraryPage() {
  const [itinerary, setItinerary] = useState<PersonalizedItineraryOutput | null>(null);
  const [formValues, setFormValues] = useState<ItineraryFormValues | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // This code runs only on the client
    const storedItinerary = sessionStorage.getItem('generatedItinerary');
    const storedFormValues = sessionStorage.getItem('formValues');

    if (storedItinerary && storedFormValues) {
      try {
        setItinerary(JSON.parse(storedItinerary));
        const parsedFormValues = JSON.parse(storedFormValues);
        // Date objects are not preserved in JSON, so we need to convert the string back to a Date
        if (parsedFormValues.date) {
            parsedFormValues.date = new Date(parsedFormValues.date);
        }
        setFormValues(parsedFormValues);
      } catch (error) {
        console.error("Failed to parse itinerary from session storage", error);
        // Clear invalid data
        sessionStorage.removeItem('generatedItinerary');
        sessionStorage.removeItem('formValues');
      }
    }
    
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return <ItineraryDetailsSkeleton />;
  }

  if (!itinerary || !formValues) {
    return (
        <div className="container mx-auto">
            <NoItineraryFound />
        </div>
    )
  }

  // To reuse ItineraryView, we need to construct a full Itinerary object
  const startDate = new Date(formValues.date);
  const fullItineraryObject: Itinerary = {
    id: 'generated',
    userProfileId: '',
    name: itinerary.title,
    startDate: startDate.toISOString(),
    endDate: addDays(startDate, formValues.duration - 1).toISOString(),
    preferences: formValues.preferences,
    skillLevel: formValues.skillLevel,
    duration: formValues.duration,
    location: formValues.location,
    generatedItinerary: {
        ...itinerary,
        days: itinerary.days.map(day => ({
            ...day,
            date: format(addDays(startDate, day.day - 1), 'yyyy-MM-dd')
        }))
    },
  };


  return (
    <div className="container mx-auto">
      <ItineraryView 
        itinerary={fullItineraryObject} 
        isEditable={false}
        preBuiltFormValues={formValues} 
      />
    </div>
  );
}
